import { Badge, Button, Group, PasswordInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ZendeskAuthentication,
  getZendeskOAuthSettings,
  getZendeskOAuthStatus,
  saveZendeskApiToken,
  startZendeskOAuth,
} from '@mygpt/app';

interface Props {
  subdomain: string;
  connectionId?: string;
  friendlyName?: string;
  authentication?: ZendeskAuthentication;
  onAuthorized: (connectionId: string) => void | Promise<void>;
  onCredentialsSaved?: () => void | Promise<void>;
}

export const ZendeskOAuth = ({
  subdomain,
  connectionId,
  friendlyName,
  authentication,
  onAuthorized,
  onCredentialsSaved,
}: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [clientMode, setClientMode] = useState('local');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [settings, setSettings] = useState<{ global_client_available: boolean; redirect_uri: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const authorized = useRef(onAuthorized);
  authorized.current = onAuthorized;
  useEffect(() => {
    mounted.current = true;
    getZendeskOAuthSettings()
      .then(({ data }) => {
        if (!mounted.current) return;
        setSettings(data);
        if (data.global_client_available) setClientMode('global');
      })
      .catch(() => {});
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const notifyError = (error: unknown) => {
    const code = (error as { detail?: string })?.detail;
    showNotification({
      color: 'red',
      message: t(
        code === 'zendesk_oauth_not_configured'
          ? 'zendesk_oauth.not_configured'
          : code === 'zendesk_oauth_same_agent_required'
          ? 'zendesk_oauth.same_agent'
          : 'zendesk_oauth.failed'
      ),
    });
  };

  const authorize = async () => {
    const popup = window.open('about:blank', '_blank', 'width=640,height=760');
    if (!popup) {
      showNotification({ color: 'red', message: t('zendesk_oauth.popup_blocked') });
      return;
    }
    popup.opener = null;
    setBusy(true);
    try {
      const { data } = await startZendeskOAuth({
        subdomain,
        connection_id: connectionId,
        friendly_name: friendlyName,
        ...(clientMode === 'local' ? { client_id: clientId.trim(), client_secret: clientSecret.trim() } : {}),
      });
      setClientSecret('');
      if (!mounted.current) {
        popup.close();
        return;
      }
      popup.location.href = data.authorization_url;
      const deadline = Date.now() + 10 * 60 * 1000;
      const poll = async () => {
        try {
          const { data: result } = await getZendeskOAuthStatus(data.request_id);
          if (!mounted.current) return;
          if (result.status === 'authorized' && result.connection_id) {
            setBusy(false);
            showNotification({ color: 'green', message: t('zendesk_oauth.success') });
            await authorized.current(result.connection_id);
            return;
          }
          if (result.status === 'failed' || result.status === 'expired' || Date.now() > deadline) throw new Error();
          // Check status before popup.closed: the callback may complete just before the user closes it.
          if (popup.closed) {
            setBusy(false);
            return;
          }
          timer.current = setTimeout(poll, 2000);
        } catch (error) {
          if (!mounted.current) return;
          setBusy(false);
          notifyError(error);
        }
      };
      timer.current = setTimeout(poll, 1000);
    } catch (error) {
      popup.close();
      if (mounted.current) {
        setBusy(false);
        notifyError(error);
      }
    }
  };

  const saveToken = async () => {
    if (!connectionId) return;
    setBusy(true);
    try {
      await saveZendeskApiToken(connectionId, { admin_email: email.trim(), api_token: token.trim() });
      setToken('');
      setShowToken(false);
      showNotification({ color: 'green', message: t('zendesk_oauth.token_saved') });
      await onCredentialsSaved?.();
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="xs">
      <Group gap="xs">
        {authentication?.oauth_configured && (
          <Badge color={authentication.oauth_last_error ? 'orange' : 'green'}>OAuth</Badge>
        )}
        {authentication?.api_token_configured && <Badge color="gray">API token</Badge>}
      </Group>
      <Text size="xs" c="dimmed">
        {t('zendesk_oauth.description')}
      </Text>
      {authentication?.oauth_last_error && (
        <Text size="xs" c="orange">
          {t('zendesk_oauth.reauthorize')}
        </Text>
      )}
      {settings?.global_client_available && (
        <Select
          label={t('zendesk_oauth.client_mode')}
          allowDeselect={false}
          value={clientMode}
          onChange={(value) => setClientMode(value || 'local')}
          data={[
            { value: 'global', label: t('zendesk_oauth.global_client') },
            { value: 'local', label: t('zendesk_oauth.local_client') },
          ]}
        />
      )}
      {clientMode === 'local' && (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            {t('zendesk_oauth.local_description')}
          </Text>
          <TextInput
            label={t('zendesk_oauth.client_id')}
            name="zendesk-oauth-client-id"
            autoComplete="off"
            value={clientId}
            onChange={(event) => setClientId(event.currentTarget.value)}
          />
          <PasswordInput
            label={t('zendesk_oauth.client_secret')}
            name="zendesk-oauth-client-secret"
            autoComplete="new-password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.currentTarget.value)}
          />
          {settings && <TextInput label={t('zendesk_oauth.redirect_uri')} readOnly value={settings.redirect_uri} />}
        </Stack>
      )}
      <Group>
        <Button
          type="button"
          variant="light"
          size="xs"
          loading={busy}
          disabled={!subdomain || (clientMode === 'local' && (!clientId.trim() || !clientSecret.trim()))}
          onClick={authorize}
        >
          {t(authentication?.oauth_configured ? 'zendesk_oauth.reauthorize' : 'zendesk_oauth.authorize')}
        </Button>
        {connectionId && (
          <Button type="button" variant="subtle" size="xs" disabled={busy} onClick={() => setShowToken(!showToken)}>
            {t('zendesk_oauth.api_token')}
          </Button>
        )}
      </Group>
      {showToken && (
        <Stack gap="xs">
          <TextInput
            label={t('publish.zendesk.admin_email')}
            name="zendesk-api-admin-email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
          <PasswordInput
            label={t('publish.zendesk.api_token')}
            name="zendesk-api-token"
            autoComplete="new-password"
            value={token}
            onChange={(event) => setToken(event.currentTarget.value)}
          />
          <Button type="button" size="xs" loading={busy} disabled={!email.trim() || !token.trim()} onClick={saveToken}>
            {t('zendesk_oauth.save_token')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
